import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import {
  debounce,
  isDesktopBreakpoint,
  mediaQueryLarge,
  onAnimationEnd,
  prefersReducedMotion,
  yieldToMainThread,
} from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    const link = this.refs.productCardLink;
    if (!link.getAttribute('href')) return '';

    const url = new URL(link.href);
    const variantOverride = this.#getProductPageVariantOverride();

    if (variantOverride?.action === 'set') {
      url.searchParams.set('variant', variantOverride.variantId);
    } else if (variantOverride?.action === 'remove') {
      url.searchParams.delete('variant');
    }

    return url.toString();
  }

  /**
   * Gets the user-selected variant ID that still needs to be synced from product-card state.
   * @returns {string | null} Variant ID or null.
   */
  getSelectedVariantId() {
    const variantOverride = this.#getProductPageVariantOverride();
    return variantOverride?.action === 'set' ? variantOverride.variantId : null;
  }

  /**
   * Gets the user-selected variant override for product-page URLs.
   * @returns {{ action: 'set', variantId: string } | { action: 'remove' } | null}
   */
  #getProductPageVariantOverride() {
    const link = this.refs.productCardLink;
    if (link.getAttribute('href')) {
      const url = new URL(link.href);
      if (url.searchParams.has('variant')) return null;
    }

    const variantId = this.#getCheckedVariantId();
    if (variantId === undefined) return null;

    return variantId ? { action: 'set', variantId } : { action: 'remove' };
  }

  /** @returns {string | undefined} */
  #getCheckedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    void this.refs.quickAdd?.prefetchProductGrid(this.productPageUrl)?.catch((error) => {
      console.error('Quick-add prefetch failed:', error);
    });
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');

    this.#connectedAbort?.abort();
    this.#connectedAbort = new AbortController();
    const opts = { signal: this.#connectedAbort.signal };

    this.#handleQuickAdd();

    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, opts);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, opts);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd, opts);

    this.addEventListener('click', this.navigateToProduct, opts);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    this.#clearPreloadTimeout();
    this.#preloadTimeoutId = setTimeout(() => {
      this.#preloadTimeoutId = null;
      if (!this.isConnected) return;
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearPreloadTimeout();
    this.#connectedAbort?.abort();
    this.#connectedAbort = null;
    this.#crossfadeGeneration += 1;
    this.#clearCrossfadeOverlay();
  }

  #clearPreloadTimeout() {
    if (this.#preloadTimeoutId == null) return;
    clearTimeout(this.#preloadTimeoutId);
    this.#preloadTimeoutId = null;
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      const opts = { signal: this.#connectedAbort?.signal };
      this.addEventListener('pointerenter', this.#fetchProductPageHandler, opts);
      this.addEventListener('focusin', this.#fetchProductPageHandler, opts);
    }
  };

  /**
   * Handles the product select event (variant selected and updated).
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    const { optionValueId, variantId, connectedProductUrl } = event.detail ?? {};
    if (optionValueId && event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(optionValueId);
    }

    if (typeof variantId === 'string') {
      this.applyVariantToLinks(variantId, typeof connectedProductUrl === 'string' ? connectedProductUrl : undefined);
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        this.updatePrice(html);
        this.#isUnavailableVariantSelected(html);
        this.#updateProductUrl(html, typeof variantId === 'string' ? variantId : undefined);
        void this.refs.quickAdd?.prefetchProductGrid(this.productPageUrl)?.catch((error) => {
          console.error('Quick-add prefetch failed:', error);
        });

        if (event.target !== this.variantPicker) {
          this.variantPicker?.updateVariantPicker(html);
        }

        this.#updateVariantImages();
        this.#previousSlideIndex = null;

        this.removeAttribute('data-no-swatch-selected');
        this.#updateOverflowList();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-card] Event promise rejected:', error);
      });
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  updatePrice(html) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update.
   * @param {Document} html - The parsed HTML document with updated variant data.
   * @param {string} [intendedVariantId] - User-intended variant.
   */
  #updateProductUrl(html, intendedVariantId) {
    const responseProductCard = html.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const responseUrl = new URL(anchorElement.href);
      this.#updateLinks({
        pathname: responseUrl.pathname,
        variantId: intendedVariantId ?? responseUrl.searchParams.get('variant'),
      });
    }
  }

  /**
   * Applies the given variant ID to the variant param of every link in the card.
   * @param {string | null} variantId - The variant ID to set, or null to remove it.
   * @param {string} [productUrl] - Product URL path to adopt.
   */
  applyVariantToLinks(variantId, productUrl) {
    let pathname;
    if (productUrl) {
      try {
        pathname = new URL(productUrl, window.location.origin).pathname;
      } catch (error) {
        console.warn('[product-card] Invalid product URL:', productUrl, error);
        return;
      }
    }

    this.#updateLinks({ pathname, variantId });
  }

  /**
   * Updates every link in the card in place, preserving existing query params.
   * @param {object} options
   * @param {string} [options.pathname] - New pathname to adopt.
   * @param {string | null} [options.variantId] - Variant ID to set, or null/undefined to remove it.
   */
  #updateLinks({ pathname, variantId }) {
    const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;
    for (const linkEl of [productCardLink, cardGalleryLink, productTitleLink]) {
      if (!(linkEl instanceof HTMLAnchorElement) || !linkEl.href) continue;
      const url = new URL(linkEl.href);
      if (pathname) url.pathname = pathname;
      if (variantId) url.searchParams.set('variant', variantId);
      else url.searchParams.delete('variant');
      linkEl.href = url.toString();
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  #isUnavailableVariantSelected(html) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (html.querySelectorAll('input:checked'));

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Syncs the card gallery to the currently selected swatch media (or primary fallback).
   * Safe to call immediately on swatch click before the section fetch completes.
   */
  syncVariantImages() {
    this.#updateVariantImages();
  }

  /**
   * Shows only the target variant-image slide (or hides all when falling back to primary).
   * @param {HTMLElement | undefined} targetSlide
   */
  #applyVariantImageVisibility(targetSlide) {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const targetId = targetSlide?.getAttribute('slide-id') ?? null;
    const { slides = [] } = slideshow.refs;

    for (const slide of slides) {
      if (slide.getAttribute('variant-image') == null) continue;

      slide.hidden = !targetId || slide.getAttribute('slide-id') !== targetId;
    }

    slideshow.resync();
  }

  /**
   * Hide the variant images that are not for the selected variant.
   * Falls back to the product featured/primary slide when media id is empty or unmatched.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!slideshow || !this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker.selectedOption.dataset.optionMediaId;
    const targetSlide = selectedImageId ? this.#findSlide(selectedImageId) : undefined;

    // Empty or unmatched media: show featured/primary — never leave the previous color visible.
    if (!targetSlide) {
      this.#selectInitialSlide();
      return;
    }

    this.#showVariantSlide(targetSlide, selectedImageId);
  }

  /**
   * Crossfades to a slide and shows only that variant-image slide.
   * @param {HTMLElement} slide
   * @param {string | number} id - The media id.
   */
  #showVariantSlide(slide, id) {
    void this.#crossfadeToSlide(slide, id, {
      prepare: () => {
        this.#applyVariantImageVisibility(slide);
      },
    });
  }

  /**
   * Finds a slide in this card's slideshow by media id.
   * @param {string | number} id - The media id. Declarative `on:` handlers parse numeric ids as numbers.
   * @returns {HTMLElement | undefined}
   */
  #findSlide(id) {
    const mediaId = String(id);
    return this.refs.slideshow?.refs.slides?.find((slide) => slide.getAttribute('slide-id') === mediaId);
  }

  /**
   * Lazy images revealed inside a clipped nested slideshow may never load on iOS Safari.
   * @param {HTMLElement} slide
   */
  #eagerLoadSlide(slide) {
    for (const image of slide.querySelectorAll('img[loading="lazy"]')) {
      image.removeAttribute('loading');
    }
  }

  /** Max wait for decode before crossfading anyway (slow networks / dense grids). */
  static #DECODE_TIMEOUT_MS = 150;

  /** @type {number} */
  #crossfadeGeneration = 0;

  /**
   * Waits for an image to decode, or gives up after {@link ProductCard.#DECODE_TIMEOUT_MS}.
   * Already-decoded images resolve immediately.
   * @param {HTMLImageElement | null | undefined} image
   */
  async #awaitImageDecode(image) {
    if (!(image instanceof HTMLImageElement)) return;
    if (image.complete && image.naturalWidth > 0) return;

    try {
      await Promise.race([
        image.decode(),
        new Promise((resolve) => {
          setTimeout(resolve, ProductCard.#DECODE_TIMEOUT_MS);
        }),
      ]);
    } catch {
      // Decode can fail for broken/cached URLs; still swap so selection stays responsive.
    }
  }

  /**
   * Resolves a slideshow index the same way `Slideshow.select` does for infinite wrap,
   * then crossfades to that slide when it has a `slide-id`.
   * @param {number} index
   * @param {{ prepare?: () => void, onSelected?: () => void }} [options]
   */
  #crossfadeToIndex(index, options = {}) {
    const { slideshow } = this.refs;
    const slides = slideshow?.slides;
    if (!slideshow || !slides?.length) return;

    let resolved = index;
    const lastIndex = slides.length - 1;

    if (resolved < 0) resolved = lastIndex;
    else if (resolved > lastIndex) resolved = 0;

    const slide = slides[resolved];
    const id = slide?.getAttribute('slide-id');

    if (!slide || !id) {
      slideshow.select(resolved, undefined, { animate: false });
      options.onSelected?.();
      return;
    }

    void this.#crossfadeToSlide(slide, id, {
      prepare: options.prepare,
    }).then(() => {
      options.onSelected?.();
    });
  }

  /**
   * Holds the previous gallery image until the target is decoded, then crossfades
   * with the theme's existing fadeIn / fadeOut keyframes.
   * @param {HTMLElement} slide - The slideshow slide to show.
   * @param {string | number} id - The media id for slideshow.select.
   * @param {{ prepare?: () => void }} [options] - Optional prep after select (e.g. hide other variant slides).
   */
  async #crossfadeToSlide(slide, id, options = {}) {
    const { slideshow, cardGallery } = this.refs;
    if (!slideshow) return;

    const mediaId = String(id);
    const currentSlide = slideshow.slides?.[slideshow.current];

    // Select before visibility prep — hiding peers first remaps slides[current] to the
    // target and select early-returns without scrolling (Key Ring nested card gallery).
    const commit = async () => {
      await slideshow.select({ id: mediaId }, undefined, { animate: false });
      options.prepare?.();
    };

    if (currentSlide === slide) {
      await commit();
      return;
    }

    if (prefersReducedMotion() || !(cardGallery instanceof HTMLElement)) {
      this.#eagerLoadSlide(slide);
      await commit();
      return;
    }

    const generation = ++this.#crossfadeGeneration;
    this.#clearCrossfadeOverlay();

    const currentImg = currentSlide?.querySelector('img');
    const targetImg = slide.querySelector('img');

    /** @type {HTMLImageElement | null} */
    let overlay = null;

    if (currentImg instanceof HTMLImageElement) {
      overlay = /** @type {HTMLImageElement} */ (currentImg.cloneNode(true));
      overlay.removeAttribute('loading');
      overlay.removeAttribute('ref');
      overlay.removeAttribute('id');
      overlay.classList.add('card-gallery__crossfade-overlay');
      cardGallery.appendChild(overlay);
    }

    this.#eagerLoadSlide(slide);
    await this.#awaitImageDecode(targetImg instanceof HTMLImageElement ? targetImg : null);

    if (generation !== this.#crossfadeGeneration) {
      overlay?.remove();
      return;
    }

    await commit();

    if (generation !== this.#crossfadeGeneration) {
      overlay?.remove();
      return;
    }

    if (targetImg instanceof HTMLImageElement) {
      targetImg.classList.remove('card-gallery__crossfade-incoming');
      // Restart fadeIn if the same image was animated on a prior swatch hop.
      void targetImg.offsetWidth;
      targetImg.classList.add('card-gallery__crossfade-incoming');
    }

    if (overlay) {
      overlay.classList.add('is-leaving');
      await onAnimationEnd(overlay);

      if (generation === this.#crossfadeGeneration) {
        overlay.remove();
        targetImg?.classList.remove('card-gallery__crossfade-incoming');
      }
    } else if (targetImg instanceof HTMLImageElement) {
      await onAnimationEnd(targetImg);

      if (generation === this.#crossfadeGeneration) {
        targetImg.classList.remove('card-gallery__crossfade-incoming');
      }
    }
  }

  /**
   * Removes any in-progress crossfade overlay / incoming classes.
   */
  #clearCrossfadeOverlay() {
    const { cardGallery } = this.refs;
    if (!(cardGallery instanceof HTMLElement)) return;

    for (const overlay of cardGallery.querySelectorAll('.card-gallery__crossfade-overlay')) {
      overlay.remove();
    }

    for (const image of cardGallery.querySelectorAll('.card-gallery__crossfade-incoming')) {
      image.classList.remove('card-gallery__crossfade-incoming');
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #preloadTimeoutId = null;

  /** @type {AbortController | null} */
  #connectedAbort = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant (desktop mouse hover only; touch uses click → variantChanged).
   * @param {string | number} id - The id of the variant to preview.
   * @param {PointerEvent} [event] - The pointer event from the declarative handler.
   */
  previewVariant(id, event) {
    if (event instanceof PointerEvent && event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    const slide = this.#findSlide(id);
    if (!slide) {
      this.#selectInitialSlide();
      return;
    }

    // Same visibility prep as click — without it, a prior selected color slide can stay
    // visible under the hover target in nested carousels (max_visible_slide_count: 1).
    this.#showVariantSlide(slide, id);
  }

  /**
   * Previews the next image (desktop mouse only; same crossfade path as swatch preview).
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      this.#crossfadeToIndex(this.#previousSlideIndex);
    } else {
      this.#crossfadeToIndex(slideshow.nextIndex, {
        onSelected: () => this.#preloadNextPreviewImage(),
      });
    }
  }

  /**
   * Resets the image to the variant image (desktop mouse only).
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      this.#crossfadeToIndex(slideshow.previousIndex);
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the selected variant media, or the featured/primary slide.
   */
  #resetVariant = () => {
    if (!this.refs.slideshow) return;

    if (this.variantPicker?.selectedOption) {
      this.#updateVariantImages();
    } else {
      this.#selectInitialSlide();
    }
  };

  /**
   * Selects the initial (primary) slide, falling back to the first visible slide.
   * Syncs variant-image visibility so a prior color photo cannot stay visible.
   */
  #selectInitialSlide() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      this.#showVariantSlide(initialSlide, slideId);
      return;
    }

    this.#applyVariantImageVisibility(undefined);
    this.#crossfadeGeneration += 1;
    this.#clearCrossfadeOverlay();
    slideshow.select(0, undefined, { animate: false });
  }

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant after swatch pointer leave (desktop mouse only).
   * @param {PointerEvent} [event] - The pointer event from the declarative handler.
   */
  resetVariant = debounce((event) => {
    if (event instanceof PointerEvent && event.pointerType !== 'mouse') return;
    this.#resetVariant();
  }, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const hasAvailableVariant = clickedSwatch.dataset.hasAvailableVariant === 'true';
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    if (isSwatchInput && hasAvailableVariant && firstAvailableVariantId) {
      event.stopPropagation();
      this.updateSelectedOption(clickedSwatch);

      // Update gallery immediately — don't wait for the section fetch.
      const productCard = this.closest('product-card');
      if (productCard instanceof ProductCard) {
        productCard.syncVariantImages();
      }

      const optionValueId = clickedSwatch.dataset.optionValueId || '';
      const connectedProductUrl = clickedSwatch.dataset.connectedProductUrl || '';
      const requestUrl = this.buildRequestUrl(clickedSwatch, 'product-card', [optionValueId]);

      this.fetchUpdatedSection(requestUrl, {
        detail: { optionValueId, variantId: firstAvailableVariantId, connectedProductUrl },
      });
      return;
    }

    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
